import { useMemo, useRef, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';

import {
  submitIntake,
  type ConsentDefinition,
  type IntakeConfig,
  type BadgeRead,
  type IntakeSubmission,
  type SurveyAnswers,
} from '../../api/intake';
import { extractErrorMessage } from '../../api/errors';
import { validateEmail } from '../../validation/profile';
import { BadgeCapture, badgeConferenceId, type BadgeCaptureResult } from './BadgeCapture';
import { SignaturePad } from './SignaturePad';
import { SurveyForm, cleanSurveyAnswers, isQuestionVisible, validateSurvey } from './SurveyForm';

type StepKey = 'badge' | 'details' | 'handover' | 'consent' | 'survey' | 'review';

const STEP_LABEL: Record<Exclude<StepKey, 'handover'>, string> = {
  badge: 'Badge',
  details: 'Details',
  consent: 'Consent',
  survey: 'Survey',
  review: 'Review',
};

interface Details {
  full_name: string;
  email: string;
  organization: string;
  city: string;
  state_region: string;
  country: string;
  title: string;
  years: string;
  npi: string;
  conference_id: string;
}

const EMPTY_DETAILS: Details = {
  full_name: '',
  email: '',
  organization: '',
  city: '',
  state_region: '',
  country: '',
  title: '',
  years: '',
  npi: '',
  conference_id: '',
};

function detailsErrors(d: Details): Partial<Record<keyof Details, string>> {
  const errs: Partial<Record<keyof Details, string>> = {};
  if (!d.full_name.trim()) errs.full_name = 'Required';
  if (d.email.trim()) {
    const e = validateEmail(d.email.trim());
    if (e) errs.email = e;
  }
  if (d.years.trim()) {
    const n = Number(d.years);
    if (!Number.isInteger(n) || n < 0 || n > 70) errs.years = 'A whole number of years, 0–70';
  }
  if (d.npi.trim() && !/^\d{10}$/.test(d.npi.trim())) errs.npi = '10 digits, or leave it blank';
  return errs;
}

/** Consent body → paragraphs (blank-line separated), with "• " lines as
 *  bullets and single newlines kept (addresses, phone numbers). */
function ConsentBody({ body }: { body: string }) {
  const blocks = body.split(/\n\s*\n/);
  return (
    <>
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        const out: ReactNode[] = [];
        let text: string[] = [];
        let bullets: string[] = [];
        const flushText = () => {
          if (text.length) {
            out.push(
              <Typography key={`t${out.length}`} sx={{ whiteSpace: 'pre-line', mb: 1.5 }}>
                {text.join('\n')}
              </Typography>,
            );
            text = [];
          }
        };
        const flushBullets = () => {
          if (bullets.length) {
            out.push(
              <Box component="ul" key={`b${out.length}`} sx={{ mt: 0, mb: 1.5, pl: 3 }}>
                {bullets.map((b, j) => (
                  <Typography component="li" key={j} sx={{ mb: 0.5 }}>
                    {b}
                  </Typography>
                ))}
              </Box>,
            );
            bullets = [];
          }
        };
        for (const line of lines) {
          const m = /^\s*•\s*(.*)$/.exec(line);
          if (m) {
            flushText();
            bullets.push(m[1]);
          } else {
            flushBullets();
            text.push(line);
          }
        }
        flushText();
        flushBullets();
        return <Box key={i}>{out}</Box>;
      })}
    </>
  );
}

function ConsentHeader({ def }: { def: ConsentDefinition }) {
  const rows: Array<[string, string | undefined]> = [
    ['Protocol director', def.protocol_director],
    ['IRB approval', def.irb_approval],
    ['IRB expiration', def.irb_expiration],
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      {def.institution && (
        <Typography variant="overline" color="text.secondary" component="div">
          {def.institution}
        </Typography>
      )}
      <Typography variant="h5" component="h2">
        {def.title}
      </Typography>
      {def.protocol_title && (
        <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
          Protocol: {def.protocol_title}
        </Typography>
      )}
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        {rows
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <Typography key={k} variant="body2" color="text.secondary">
              {k}: <strong>{v}</strong>
            </Typography>
          ))}
      </Stack>
    </Paper>
  );
}

function errorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

function conflictUserId(err: unknown): string | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const id = (detail as { participant_user_id?: unknown }).participant_user_id;
    return typeof id === 'string' ? id : null;
  }
  return null;
}

/**
 * The booth flow (CONFERENCE-INTAKE), full screen for a tablet: badge photo →
 * details (operator) → hand-over → consent → survey (participant) → review &
 * submit, which creates the participant and their intake record in one call.
 * Everything stays in memory until submit; closing mid-way asks first.
 */
export function IntakeWizard({
  open,
  tenantId,
  config,
  onClose,
  onEnrolled,
  participantName,
  selfService = false,
  submitFn,
  readBadgeFn,
  title = 'Enroll at booth',
}: {
  open: boolean;
  tenantId: string;
  config: IntakeConfig;
  onClose: () => void;
  onEnrolled: (participantUserId: string) => void;
  /** The participant fills it in on their own phone (BOOTH-SELF-ENROL): no
   *  hand-over step, no close button, wording addressed to them. */
  selfService?: boolean;
  /** Where the submission goes; default = the staff booth route. */
  submitFn?: (body: IntakeSubmission) => Promise<{ participant_user_id?: string } | void>;
  /** How the badge is read; default = the staff route. */
  readBadgeFn?: (photo: string) => Promise<BadgeRead>;
  title?: string;
  /** Resolves an existing participant's name for the "already enrolled"
   *  message; the id is shown when it returns nothing. */
  participantName?: (userId: string) => string | null | undefined;
}) {
  const consentForm = config.consent_form;
  const surveyForm = config.survey_form;
  const consent = consentForm?.definition ?? null;
  const survey = surveyForm?.definition ?? null;

  const steps = useMemo<StepKey[]>(() => {
    const s: StepKey[] = ['badge', 'details'];
    if ((consent || survey) && !selfService) s.push('handover');
    if (consent) s.push('consent');
    if (survey) s.push('survey');
    s.push('review');
    return s;
  }, [consent, survey, selfService]);
  const stepperKeys = steps.filter((s) => s !== 'handover') as Array<Exclude<StepKey, 'handover'>>;

  const [step, setStep] = useState<StepKey>('badge');
  const [badge, setBadge] = useState<BadgeCaptureResult | null>(null);
  const [details, setDetails] = useState<Details>(EMPTY_DETAILS);
  const [detailsTried, setDetailsTried] = useState(false);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [initials, setInitials] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [signedName, setSignedName] = useState('');
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [surveyTried, setSurveyTried] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ id: string | null } | null>(null);
  const [enrolled, setEnrolled] = useState<{ id: string; name: string } | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const reset = () => {
    setStep('badge');
    setBadge(null);
    setDetails(EMPTY_DETAILS);
    setDetailsTried(false);
    setChoices({});
    setInitials('');
    setSignature(null);
    setSignedName('');
    setAnswers({});
    setSurveyTried(false);
    setSubmitting(false);
    setSubmitError(null);
    setConflict(null);
    setEnrolled(null);
    setConfirmClose(false);
  };

  const dirty = !enrolled && (step !== 'badge' || badge !== null);

  const close = () => {
    reset();
    onClose();
  };
  const requestClose = () => {
    if (submitting) return;
    if (dirty) setConfirmClose(true);
    else close();
  };

  const go = (to: StepKey) => {
    setStep(to);
    contentRef.current?.scrollTo?.({ top: 0 });
  };
  const idx = steps.indexOf(step);
  const next = () => go(steps[Math.min(idx + 1, steps.length - 1)]);
  const back = () => go(steps[Math.max(idx - 1, 0)]);

  // ── Badge → details pre-fill ──
  const onBadge = (r: BadgeCaptureResult | null) => {
    setBadge(r);
    if (!r) return;
    const read = r.read;
    setDetails((d) => ({
      ...d,
      full_name: read?.full_name ?? d.full_name,
      organization: read?.organization ?? d.organization,
      city: read?.city ?? d.city,
      state_region: read?.state_region ?? d.state_region,
      country: read?.country ?? d.country,
      conference_id: badgeConferenceId(r) ?? d.conference_id,
    }));
  };

  const setField = (k: keyof Details) => (e: { target: { value: string } }) =>
    setDetails((d) => ({ ...d, [k]: e.target.value }));
  const dErrors = detailsErrors(details);
  const shownDErrors = detailsTried ? dErrors : {};

  // ── Consent ──
  const needsInitials = Boolean(consent?.choices.some((c) => c.initials));
  const consentMissing: string[] = [];
  if (consent) {
    if (consent.choices.some((c) => !c.options.includes(choices[c.id] ?? ''))) {
      consentMissing.push(consent.choices.length > 1 ? 'answer each question' : 'answer Yes or No');
    }
    if (needsInitials && !initials.trim()) consentMissing.push('add your initials');
    if (!signature) consentMissing.push('sign');
    if (!signedName.trim()) consentMissing.push('print your name');
  }

  // ── Survey ──
  const surveyErrors = survey ? validateSurvey(survey, answers) : {};
  const surveyOk = Object.keys(surveyErrors).length === 0;

  const today = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setConflict(null);
    const years = details.years.trim();
    const blank = (s: string) => s.trim() || null;
    const body: IntakeSubmission = {
      participant: {
        full_name: details.full_name.trim(),
        email: blank(details.email),
        title: blank(details.title),
        years_of_experience: years ? Number(years) : null,
        city: blank(details.city),
        state_region: blank(details.state_region),
        country: blank(details.country),
        npi: blank(details.npi),
      },
      conference_id: blank(details.conference_id),
      organization: blank(details.organization),
      badge_photo: badge?.photo ?? null,
      badge_read: badge?.read ?? null,
      consent:
        consentForm && signature
          ? {
              form_id: consentForm.id,
              choices,
              initials: initials.trim() || null,
              signed_name: signedName.trim(),
              signature,
            }
          : null,
      survey: surveyForm && survey
        ? { form_id: surveyForm.id, answers: cleanSurveyAnswers(survey, answers) }
        : null,
    };
    try {
      const res = submitFn ? await submitFn(body) : await submitIntake(tenantId, body);
      const id = (res && res.participant_user_id) || '';
      setEnrolled({ id, name: body.participant.full_name });
      onEnrolled(id);
    } catch (err) {
      if (errorStatus(err) === 409 && conflictUserId(err) !== null) {
        setConflict({ id: conflictUserId(err) });
      } else {
        setSubmitError(extractErrorMessage(err, 'could not enroll the participant'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Rendering ──
  const renderStep = () => {
    switch (step) {
      case 'badge':
        return (
          <BadgeCapture
            tenantId={tenantId}
            read={readBadgeFn}
            value={badge}
            onChange={onBadge}
            onSkip={() => {
              setBadge(null);
              go('details');
            }}
          />
        );

      case 'details': {
        const field = (
          k: keyof Details,
          label: string,
          extra: { required?: boolean; type?: string; helper?: string; md?: number } = {},
        ) => (
          <Grid size={{ xs: 12, md: extra.md ?? 6 }}>
            <TextField
              label={label}
              value={details[k]}
              onChange={setField(k)}
              required={extra.required}
              type={extra.type}
              error={Boolean(shownDErrors[k])}
              helperText={shownDErrors[k] ?? extra.helper}
              fullWidth
              slotProps={k === 'years' || k === 'npi' ? { htmlInput: { inputMode: 'numeric' } } : undefined}
            />
          </Grid>
        );
        return (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" component="h2">
                Participant details
              </Typography>
              <Typography color="text.secondary">
                {badge
                  ? 'Filled in from the badge — check and correct anything that was misread.'
                  : selfService
                    ? 'Type in your details. Only your name is required.'
                    : 'Type in the participant’s details. Only the name is required.'}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {field('full_name', 'Full name', { required: true, md: 12 })}
              {field('email', 'Email', {
                type: 'email',
                helper: selfService
                  ? 'Optional — we’ll email you a thank-you after your skill test'
                  : 'Optional — only if they want a portal login later',
              })}
              {field('organization', 'Organisation / hospital')}
              {field('city', 'City', { md: 4 })}
              {field('state_region', 'State / region', { md: 4 })}
              {field('country', 'Country', { md: 4 })}
              {field('title', 'Job title', { helper: 'e.g. Attending surgeon, PGY-3 resident' })}
              {field('years', 'Years of experience', { type: 'number' })}
              {field('npi', 'NPI', {
                helper: 'Optional — US clinicians: the 10-digit National Provider Identifier',
              })}
              {field('conference_id', 'Conference ID', {
                helper: badge?.qr ? 'From the badge QR code' : 'The attendee ID on the badge, if any',
              })}
            </Grid>
          </Stack>
        );
      }

      case 'handover':
        return (
          <Stack spacing={3} alignItems="center" textAlign="center" sx={{ py: { xs: 4, md: 8 } }}>
            <TabletAndroidIcon sx={{ fontSize: 96, color: 'primary.main' }} />
            <Typography variant="h4" component="h2">
              Please hand the tablet to the participant
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 520 }}>
              {details.full_name.trim() ? `${details.full_name.trim()}, ` : ''}
              {consent
                ? 'please read the consent form, then sign it on the screen'
                : 'please answer a few questions'}
              {consent && survey ? ' and answer a short survey.' : '.'}
            </Typography>
            <Button variant="contained" size="large" onClick={next} sx={{ minHeight: 64, px: 5, fontSize: '1.15rem' }}>
              Start
            </Button>
          </Stack>
        );

      case 'consent':
        if (!consent) return null;
        return (
          <Stack spacing={3}>
            <ConsentHeader def={consent} />
            {consent.sections.map((s, i) => (
              <Box key={i}>
                {s.heading && (
                  <Typography variant="h6" component="h3" gutterBottom>
                    {s.heading}
                  </Typography>
                )}
                <ConsentBody body={s.body} />
              </Box>
            ))}
            <Divider />
            {consent.choices.map((c) => (
              <Paper key={c.id} variant="outlined" sx={{ p: 2.5 }}>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 500, mb: 1.5 }}>{c.label}</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <ToggleButtonGroup
                    exclusive
                    color="primary"
                    value={choices[c.id] ?? null}
                    onChange={(_, v: string | null) => {
                      if (v) setChoices((prev) => ({ ...prev, [c.id]: v }));
                    }}
                    aria-label={c.label}
                  >
                    {c.options.map((opt) => (
                      <ToggleButton
                        key={opt}
                        value={opt}
                        sx={{ minWidth: 120, minHeight: 56, fontSize: '1.1rem', textTransform: 'none' }}
                      >
                        {opt}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {c.initials && (
                    <TextField
                      label="Initials"
                      value={initials}
                      onChange={(e) => setInitials(e.target.value.slice(0, 12))}
                      sx={{ width: 140 }}
                      slotProps={{ htmlInput: { autoCapitalize: 'characters', maxLength: 12 } }}
                    />
                  )}
                </Stack>
              </Paper>
            ))}
            {consent.copy_note && (
              <Typography variant="body2" color="text.secondary">
                {consent.copy_note}
              </Typography>
            )}
            {signature ? (
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">{consent.signature_label}</Typography>
                <Box
                  component="img"
                  src={signature}
                  alt="Your signature"
                  sx={{ maxWidth: '100%', height: 160, objectFit: 'contain', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#fff', alignSelf: 'flex-start' }}
                />
                <Box>
                  <Button onClick={() => setSignature(null)}>Sign again</Button>
                </Box>
              </Stack>
            ) : (
              <SignaturePad label={consent.signature_label} onChange={setSignature} />
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  label={consent.printed_name_label}
                  value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField label="Date" value={today} fullWidth slotProps={{ input: { readOnly: true } }} />
              </Grid>
            </Grid>
          </Stack>
        );

      case 'survey':
        if (!survey) return null;
        return (
          <Stack spacing={2}>
            <SurveyForm
              definition={survey}
              answers={answers}
              onChange={setAnswers}
              errors={surveyTried ? surveyErrors : {}}
            />
            {surveyTried && !surveyOk && (
              <Alert severity="warning">Please check the highlighted answers.</Alert>
            )}
          </Stack>
        );

      case 'review': {
        const rows: Array<[string, string]> = (
          [
            ['Name', details.full_name],
            ['Email', details.email],
            ['Organisation', details.organization],
            ['Location', [details.city, details.state_region, details.country].filter((x) => x.trim()).join(', ')],
            ['Job title', details.title],
            ['Years of experience', details.years],
            ['NPI', details.npi],
            ['Conference ID', details.conference_id],
          ] as Array<[string, string]>
        ).filter(([, v]) => v.trim());
        const answered = survey ? Object.keys(cleanSurveyAnswers(survey, answers)).length : 0;
        // Only questions the participant could see: a follow-up hidden by its
        // condition isn't one they skipped (review INTAKE-3).
        const askable = survey
          ? survey.questions.filter((q) => q.type !== 'heading' && isQuestionVisible(q, answers))
              .length
          : 0;
        return (
          <Stack spacing={3}>
            <Typography variant="h5" component="h2">
              Review and enroll
            </Typography>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Box
                component="dl"
                sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 3, rowGap: 1 }}
              >
                {rows.map(([k, v]) => (
                  <Box key={k} sx={{ display: 'contents' }}>
                    <Typography component="dt" color="text.secondary">
                      {k}
                    </Typography>
                    <Typography component="dd" sx={{ m: 0 }}>
                      {v}
                    </Typography>
                  </Box>
                ))}
                <Typography component="dt" color="text.secondary">
                  Badge photo
                </Typography>
                <Typography component="dd" sx={{ m: 0 }}>
                  {badge ? 'Attached' : 'None'}
                </Typography>
                {consent && (
                  <>
                    {consent.choices.map((c) => (
                      <Box key={c.id} sx={{ display: 'contents' }}>
                        <Typography component="dt" color="text.secondary">
                          {c.label}
                        </Typography>
                        <Typography component="dd" sx={{ m: 0 }}>
                          {choices[c.id]}
                          {c.initials && initials.trim() ? ` (initialled ${initials.trim()})` : ''}
                        </Typography>
                      </Box>
                    ))}
                    <Typography component="dt" color="text.secondary">
                      Consent
                    </Typography>
                    <Typography component="dd" sx={{ m: 0 }}>
                      Signed by {signedName.trim()} on {today}
                    </Typography>
                  </>
                )}
                {survey && (
                  <>
                    <Typography component="dt" color="text.secondary">
                      Survey
                    </Typography>
                    <Typography component="dd" sx={{ m: 0 }}>
                      {answered} of {askable} questions answered
                    </Typography>
                  </>
                )}
              </Box>
            </Paper>
            {conflict && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" onClick={() => go('details')}>
                    Edit details
                  </Button>
                }
              >
                This badge is already enrolled
                {(() => {
                  const name = conflict.id ? participantName?.(conflict.id) : null;
                  return name ? ` — as ${name}` : conflict.id ? ` (participant ${conflict.id})` : '';
                })()}
                . Check the conference ID, or clear it to enroll them again.
              </Alert>
            )}
            {submitError && <Alert severity="error">{submitError}</Alert>}
          </Stack>
        );
      }
    }
  };

  // Footer: what "next" means on each step.
  let primary: ReactNode = null;
  if (!enrolled) {
    if (step === 'badge') {
      primary = (
        <Button variant="contained" size="large" onClick={next} disabled={!badge}>
          Continue
        </Button>
      );
    } else if (step === 'details') {
      primary = (
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            setDetailsTried(true);
            if (Object.keys(dErrors).length === 0) {
              if (!signedName.trim()) setSignedName(details.full_name.trim());
              next();
            }
          }}
        >
          Continue
        </Button>
      );
    } else if (step === 'consent') {
      primary = (
        <Button variant="contained" size="large" onClick={next} disabled={consentMissing.length > 0}>
          I agree — continue
        </Button>
      );
    } else if (step === 'survey') {
      primary = (
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            setSurveyTried(true);
            if (surveyOk) next();
          }}
        >
          Continue
        </Button>
      );
    } else if (step === 'review') {
      primary = (
        <Button variant="contained" size="large" onClick={() => void submit()} disabled={submitting}>
          {submitting ? 'Sending…' : selfService ? 'Submit' : 'Enroll participant'}
        </Button>
      );
    }
  }

  const activeIdx = enrolled
    ? stepperKeys.length
    : stepperKeys.indexOf((step === 'handover' ? steps[idx + 1] : step) as Exclude<StepKey, 'handover'>);

  return (
    <Dialog open={open} onClose={requestClose} fullScreen aria-labelledby="intake-wizard-title">
      <Toolbar sx={{ borderBottom: 1, borderColor: 'divider', gap: 2 }}>
        <Typography id="intake-wizard-title" variant="h6" component="h1" sx={{ flex: 1 }}>
          {title}
        </Typography>
        {!selfService && (
          <IconButton edge="end" aria-label="Close" onClick={requestClose} disabled={submitting}>
            <CloseIcon />
          </IconButton>
        )}
      </Toolbar>
      <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stepper activeStep={activeIdx} alternativeLabel>
          {stepperKeys.map((k) => (
            <Step key={k}>
              <StepLabel>{STEP_LABEL[k]}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <DialogContent ref={contentRef}>
        <Container maxWidth="md" sx={{ py: 2 }}>
          {enrolled ? (
            <Stack spacing={3} alignItems="center" textAlign="center" sx={{ py: { xs: 4, md: 8 } }}>
              <CheckCircleIcon sx={{ fontSize: 96, color: 'success.main' }} />
              <Typography variant="h4" component="h2">
                {selfService ? `Thank you, ${enrolled.name}!` : `Enrolled ${enrolled.name}`}
              </Typography>
              <Typography color="text.secondary">
                {selfService
                  ? 'You’re enrolled — the team at the booth will take it from here. You can close this page.'
                  : 'Thank you! Please hand the tablet back.'}
              </Typography>
              {!selfService && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button variant="contained" size="large" onClick={reset} sx={{ minHeight: 56 }}>
                  Enroll the next participant
                </Button>
                <Button variant="outlined" size="large" onClick={close} sx={{ minHeight: 56 }}>
                  Close
                </Button>
              </Stack>
              )}
            </Stack>
          ) : (
            renderStep()
          )}
        </Container>
      </DialogContent>
      {!enrolled && step !== 'handover' && (
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
          {step === 'consent' && consentMissing.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
              To continue: {consentMissing.join(', ')}.
            </Typography>
          )}
          {idx > 0 && (
            <Button size="large" onClick={back} disabled={submitting}>
              Back
            </Button>
          )}
          {primary}
        </DialogActions>
      )}
      {!enrolled && step === 'handover' && (
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
          <Button size="large" onClick={back}>
            Back
          </Button>
        </DialogActions>
      )}

      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard this enrollment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Nothing has been saved yet — closing now loses the details
            {consent ? ', the signed consent' : ''}
            {survey ? ' and the survey answers' : ''} entered so far.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep going</Button>
          <Button color="error" variant="contained" onClick={close}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
